import { Paper } from '@mui/material';
import axios from 'axios';
import useAuthz from 'hooks/useAuthz';
import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useNavigate } from 'react-router';
import ClipLoader from 'react-spinners/ClipLoader';
import ActionsRequestPassRenderer from '../../components/AgGrid/ActionsRequestPassRenderer';
import RequesterRenderer from '../../components/AgGrid/RequesterRenderer';
import StatusRenderer from '../../components/AgGrid/StatusRenderer';
import { logError } from '../../components/Utils/FormatData';
import { URL_API } from '../../utils/globalConfig';
import BooleanRenderer from '../../components/AgGrid/BooleanRenderer';
import MyButton from '../../components/Layout/MyButton';
import TopBar from '../../components/Layout/TopBar';
import AgGrid from '../../components/AgGrid/AgGrid';
import MySelect from '../../components/Layout/MySelect';
import SelectorTypeDemandePassage from './SelectorTypeDemandePassage';
import RequestPassDateRenderer from '../../components/AgGrid/RequestPassDateRenderer';
import moment from 'moment';
import { dateComparator, dateFilterComparator } from '../../components/Utils/DateComparator';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import RequestPassAlertRenderer from '../../components/AgGrid/RequestPassAlertRenderer';
import { PROFIL, TRJT_TYPE } from '../../constants/enum';
import MyDialog from '../../components/Layout/MyDialog';
import DossierIncompleteRenderer from '../../components/AgGrid/DossierIncompleteRenderer';

const currentPage = 'demande-passage';

const ListeDemandesPassage = (props) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [authz, user, profil] = useAuthz();
  const [isLoading, setIsLoading] = useState(true);
  const [demandesPassages, setDemandesPassages] = useState([]);
  const [needReload, setNeedReload] = useState(false);
  const [continueModal, setContinueModal] = useState(false);
  const [agGridOptions, setAgGridOptions] = useState({
    suppressCellSelection: true,
    suppressHorizontalScroll: false,
    animateRows: true,
  });
  const frameworkComponents = {
    actionsRequestPassRenderer: ActionsRequestPassRenderer,
    requesterRenderer: RequesterRenderer,
    statutRenderer: StatusRenderer,
    dossierIncompleteRenderer: DossierIncompleteRenderer,
    booleanRenderer: BooleanRenderer,
    requestPassDateRenderer: RequestPassDateRenderer,
    requestPassAlertRenderer: RequestPassAlertRenderer,
  };

  const largeCellsStyle = { 'justify-content': 'flex-start', 'overflow': 'hidden' }

  const passageSuperieur = [
    { headerName: 'N° dossier', field: 'id', sortable: true, flex: 1, minWidth: 150, filter: 'agNumberColumnFilter' },
    {
      headerName: 'ref ddt',
      field: 'metadata.reference.numDossier',
      flex: 2,
      sortable: true,
      minWidth: 200,
      valueGetter: (params) => (params.data.metadata.reference.numDossier ? params.data.metadata.reference.numDossier : '-'),
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'alerte',
      field: 'metadata.tracingDossier.alert',
      sortable: true,
      flex: 1,
      minWidth: 130,
      cellRenderer: 'requestPassAlertRenderer',
      cellRendererParams: { setNeedReload: setNeedReload },
      profile: [1, 2],
    },
    {
      headerName: 'département',
      field: 'metadata.trajetDescr',
      sortable: true,
      flex: 2,
      minWidth: 150,
      filter: 'agNumberColumnFilter',
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let departments = [];

        if (params.data.metadata.reference.departments && params.data.metadata.reference.departments.length > 0) {
          params.data.metadata.reference.departments.forEach(function(department) {
            if (!departments.includes(department)) {
              departments.push(department);
            }
          });
        }

        return departments;
      },
    },
    {
      headerName: 'mandataire',
      field: 'metadata.complementIdentite.mandataire.transporteur',
      flex: 2,
      sortable: true,
      minWidth: 200,
      valueGetter: (params) => (params.data.metadata.complementIdentite.mandataire?.transporteur ? params.data.metadata.complementIdentite.mandataire.transporteur : '-'),
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'pétitionnaire',
      field: 'metadata.complementIdentite.transporteur',
      flex: 2,
      sortable: true,
      minWidth: 200,
      cellRenderer: 'requesterRenderer',
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => (params.data.metadata.complementIdentite.transporteur ? params.data.metadata.complementIdentite.transporteur : '-'),
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'axe',
      field: 'metadata.trajetDescr.descr',
      flex: 2,
      sortable: true,
      minWidth: 200,
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let trajetDescr  = params.data.metadata.trajetDescr;
        let aller = trajetDescr.descr;
        let axis = "";

        aller.map((a) => {
          axis += a.road + ' ';
        });

        if (trajetDescr.type === TRJT_TYPE.ALLER_RETOUR) {
          let retour = trajetDescr.descrRetour;
          axis += ' / ';
          retour.map((r) => {
            if (!r.road) return;
            axis += r.road + ' ';
          });
        }

        return axis ?? '-';
      },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'trajet',
      field: 'metadata.trajetDescr',
      flex: 2,
      sortable: true,
      minWidth: 200,
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let depart = params.data.metadata.trajetDescr.depart;
        let arrivee = params.data.metadata.trajetDescr.arrivee;

        return depart + ' - ' + arrivee;
      },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'tronçons',
      field: 'metadata.trajetDescr',
      flex: 2,
      sortable: true,
      minWidth: 600,
      cellStyle: largeCellsStyle,
      valueGetter: (params) => {
        let trajetDescr  = params.data.metadata.trajetDescr;
        let aller = trajetDescr.descr;
        let troncons = "";

        aller.map((a) => {
          troncons += a.nomSection + ' ';
        });

        if (trajetDescr.type === TRJT_TYPE.ALLER_RETOUR) {
          let retour = trajetDescr.descrRetour;
          troncons += ' / ';
          retour.map((r) => {
            if (!r.nomSection) return;
            troncons += r.nomSection + ' ';
          });
        }

        return troncons ?? '-';
      },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'date réception',
      field: 'metadata.tracingDossier.receptionDate',
      flex: 2,
      minWidth: 200,
      sortable: true,
      valueGetter: (params) => {
        const raw = params.data?.metadata.tracingDossier?.receptionDate;
        if (!raw) return null;
        return new Date(raw.replace(' ', 'T'));
      },
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return moment(params.value).format('DD/MM/YYYY HH:mm');
      },
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: { comparator: dateFilterComparator },
    },
    {
      headerName: 'date prise en charge',
      field: 'metadata.tracingDossier.pickUpDate',
      flex: 2,
      sortable: true,
      minWidth: 225,
      valueGetter: (params) => {
        const raw = params.data?.metadata.tracingDossier?.pickUpDate;
        if (!raw) return null;
        return new Date(raw.replace(' ', 'T'));
      },
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return moment(params.value).format('DD/MM/YYYY HH:mm');
      },
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: { comparator: dateFilterComparator },
    },
    {
      headerName: 'centres',
      field: 'metadata.concerned_centers',
      sortable: true,
      flex: 3,
      minWidth: 600,
      filter: 'agTextColumnFilter',
      cellStyle: largeCellsStyle,
      valueGetter: (params) => {
        let centres = [];

        if (params.data.metadata.complInfo && params.data.metadata.complInfo.refCtr && params.data.metadata.complInfo.refCtr.length > 0) {
          params.data.metadata.complInfo.refCtr.forEach(function(centre) {
            if (!centres.includes(centre.name)) {
              centres.push(centre.name);
            }
          });
        }

        return centres;
      },
    },
    {
      headerName: 'catégorie',
      field: 'metadata.category',
      sortable: true,
      flex: 1,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => (params.data.metadata.caracConvoi.category ?? '-'),
    },
    {
      headerName: 'desc',
      field: 'metadata.reference_desc',
      sortable: true,
      flex: 3,
      minWidth: 200,
      filter: 'agTextColumnFilter',
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let references = [];

        if (params.data.metadata.complInfo.desc1Number) {
          references.push('N° ' + params.data.metadata.complInfo.desc1Number);
        }

        if (params.data.metadata.complInfo.desc2Number) {
          references.push('N° ' + params.data.metadata.complInfo.desc2Number);
        }

        references = references.join(', ');

        return references;
      }
    },
    {
      headerName: 'accompagnement',
      field: 'metadata.caracConvoi.accompagnement',
      sortable: true,
      flex: 2,
      minWidth: 175,
      cellRenderer: 'booleanRenderer',
      cellRendererParams: {
        displayLabel: true,
      },
    },
    {
      headerName: 'statut',
      field: 'status.libelle',
      sortable: true,
      width: 200,
      pinned: 'right',
      cellRenderer: 'statutRenderer',
      cellStyle: { 'justify-content': 'flex-start' },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'actions',
      field: '',
      sortable: false,
      cellRenderer: 'actionsRequestPassRenderer',
      cellRendererParams: { setNeedReload: setNeedReload },
      pinned: 'right',
      cellStyle: { 'justify-content': 'flex-start' },
      minWidth: 250,
    },
  ];
  const passageAutoroute = [
    { headerName: 'N° dossier', field: 'id', sortable: true, flex: 1, minWidth: 150, filter: 'agNumberColumnFilter' },
    {
      headerName: 'ref ddt',
      field: 'metadata.reference.numDossier',
      flex: 2,
      sortable: true,
      minWidth: 200,
      valueGetter: (params) => (params.data.metadata.reference.numDossier ? params.data.metadata.reference.numDossier : '-'),
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'alerte',
      field: 'metadata.tracingDossier.alert',
      sortable: true,
      flex: 1,
      minWidth: 130,
      cellRenderer: 'requestPassAlertRenderer',
      cellRendererParams: { setNeedReload: setNeedReload },
      profile: [1, 2],
    },
    {
      headerName: 'département',
      field: 'metadata.trajetDescr',
      sortable: true,
      flex: 2,
      minWidth: 150,
      filter: 'agNumberColumnFilter',
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let departments = [];

        if (params.data.metadata.reference.departments && params.data.metadata.reference.departments.length > 0) {
          params.data.metadata.reference.departments.forEach(function(department) {
            if (!departments.includes(department)) {
              departments.push(department);
            }
          });
        }

        return departments;
      },
    },
    {
      headerName: 'mandataire',
      field: 'metadata.complementIdentite.mandataire.transporteur',
      flex: 2,
      sortable: true,
      minWidth: 200,
      valueGetter: (params) => (params.data.metadata.complementIdentite.mandataire?.transporteur ? params.data.metadata.complementIdentite.mandataire.transporteur : '-'),
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'pétitionnaire',
      field: 'metadata.complementIdentite.transporteur',
      flex: 2,
      sortable: true,
      minWidth: 200,
      cellRenderer: 'requesterRenderer',
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => (params.data.metadata.complementIdentite.transporteur ? params.data.metadata.complementIdentite.transporteur : '-'),
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'axe',
      field: 'metadata.trajetDescr.descr',
      flex: 2,
      sortable: true,
      minWidth: 200,
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let trajetDescr  = params.data.metadata.trajetDescr;
        let aller = trajetDescr.descr;
        let axis = "";

        aller.map((a) => {
          axis += a.road + ' ';
        });

        if (trajetDescr.type === TRJT_TYPE.ALLER_RETOUR) {
          let retour = trajetDescr.descrRetour;
          axis += ' / ';
          retour.map((r) => {
            if (!r.road) return;
            axis += r.road + ' ';
          });
        }

        return axis ?? '-';
      },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'trajet',
      field: 'metadata.trajetDescr',
      flex: 2,
      sortable: true,
      minWidth: 200,
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let depart = params.data.metadata.trajetDescr.depart;
        let arrivee = params.data.metadata.trajetDescr.arrivee;

        return depart + ' - ' + arrivee;
      },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'tronçons',
      field: 'metadata.trajetDescr',
      flex: 2,
      sortable: true,
      minWidth: 600,
      cellStyle: largeCellsStyle,
      valueGetter: (params) => {
        let trajetDescr  = params.data.metadata.trajetDescr;
        let aller = trajetDescr.descr;
        let troncons = "";

        aller.map((a) => {
          troncons += a.nomSection + ' ';
        });

        if (trajetDescr.type === TRJT_TYPE.ALLER_RETOUR) {
          let retour = trajetDescr.descrRetour;
          troncons += ' / ';
          retour.map((r) => {
            if (!r.nomSection) return;
            troncons += r.nomSection + ' ';
          });
        }

        return troncons ?? '-';
      },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'date réception',
      field: 'metadata.tracingDossier.receptionDate',
      flex: 2,
      minWidth: 200,
      sortable: true,
      valueGetter: (params) => {
        const raw = params.data?.metadata.tracingDossier?.receptionDate;
        if (!raw) return null;
        return new Date(raw.replace(' ', 'T'));
      },
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return moment(params.value).format('DD/MM/YYYY HH:mm');
      },
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: { comparator: dateFilterComparator },
    },
    {
      headerName: 'date prise en charge',
      field: 'metadata.tracingDossier.pickUpDate',
      flex: 2,
      sortable: true,
      minWidth: 225,
      valueGetter: (params) => {
        const raw = params.data?.metadata.tracingDossier?.pickUpDate;
        if (!raw) return null;
        return new Date(raw.replace(' ', 'T'));
      },
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return moment(params.value).format('DD/MM/YYYY HH:mm');
      },
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: { comparator: dateFilterComparator },
    },
    {
      headerName: 'centres',
      field: 'metadata.concerned_centers',
      sortable: true,
      flex: 3,
      minWidth: 600,
      filter: 'agTextColumnFilter',
      cellStyle: largeCellsStyle,
      valueGetter: (params) => {
        let centres = [];

        if (params.data.metadata.complInfo && params.data.metadata.complInfo.refCtr && params.data.metadata.complInfo.refCtr.length > 0) {
          params.data.metadata.complInfo.refCtr.forEach(function(centre) {
            if (!centres.includes(centre.name)) {
              centres.push(centre.name);
            }
          });
        }

        return centres;
      },
    },
    {
      headerName: 'catégorie',
      field: 'metadata.category',
      sortable: true,
      flex: 1,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => (params.data.metadata.caracConvoi.category ?? '-'),
    },
    {
      headerName: 'desc',
      field: 'metadata.reference_desc',
      sortable: true,
      flex: 3,
      minWidth: 200,
      filter: 'agTextColumnFilter',
      cellStyle: { 'justify-content': 'flex-start' },
      valueGetter: (params) => {
        let references = [];

        if (params.data.metadata.complInfo.desc1Number) {
          references.push('N° ' + params.data.metadata.complInfo.desc1Number);
        }

        if (params.data.metadata.complInfo.desc2Number) {
          references.push('N° ' + params.data.metadata.complInfo.desc2Number);
        }

        references = references.join(', ');

        return references;
      }
    },
    {
      headerName: 'accompagnement',
      field: 'metadata.caracConvoi.accompagnement',
      sortable: true,
      flex: 2,
      minWidth: 175,
      cellRenderer: 'booleanRenderer',
      cellRendererParams: {
        displayLabel: true,
      },
    },
    {
      headerName: 'statut',
      field: 'status.libelle',
      sortable: true,
      minWidth: 200,
      pinned: 'right',
      cellRenderer: 'statutRenderer',
      cellStyle: { 'justify-content': 'flex-start' },
      filter: 'agTextColumnFilter',
    },
    {
      headerName: '',
      field: 'incomplete',
      sortable: true,
      flex: 0.6,
      minWidth: 30,
      maxWidth: 50,
      pinned: 'right',
      cellStyle: { 'justify-content': 'flex-start' },
      cellRenderer: 'dossierIncompleteRenderer',
    },
    {
      headerName: 'actions',
      field: '',
      sortable: false,
      cellRenderer: 'actionsRequestPassRenderer',
      cellRendererParams: { setNeedReload: setNeedReload },
      pinned: 'right',
      cellStyle: { 'justify-content': 'flex-start' },
      minWidth: 250,
    },
  ];

  const [columns, setColumns] = useState(passageSuperieur);
  const [centers, setCenters] = useState([]);
  const [requestType, setRequestType] = useState('superieur');
  const [selectedValue, setSelectedValue] = useState({ centerSelected: [] });
  const [pageFullyLoaded, setPageFullyLoaded] = useState(false);

  useEffect(() => {
    if (profil.profil === 6) {
      alert.error(`Vous n'êtes pas autorisé à accéder à la liste des dossiers`);
      navigate('/');
    } else {
      document.title = 'TE - Liste des dossiers';

      apiGetListCenters();
      apiGetBlacklistedUsers();
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (needReload) {
      apiGetListRequestPass();
      setNeedReload(false);
    }
  }, [needReload]);

  useEffect(() => {
    let localStorageFilters = JSON.parse(window.localStorage.getItem('filters'));

    if (!localStorageFilters || !localStorageFilters[currentPage]) {
      updateLocalStorageFilters({
        requestType: requestType,
        selectedValue: selectedValue,
      });
    } else {
      setRequestType(localStorageFilters[currentPage].requestType);
      setSelectedValue(localStorageFilters[currentPage].selectedValue);
    }
  }, [pageFullyLoaded]);

  useEffect(() => {
    setIsLoading(true);

    // Changement des colonnes AgGrid
    if (requestType === 'superieur') {
      setColumns(passageSuperieur);
    } else if (requestType === 'autoroute') {
      setColumns(passageAutoroute);
    }

    apiGetListRequestPass();

  }, [requestType, selectedValue]);

  const handleOnChange = (newRequestType) => {
    setRequestType(newRequestType);
    updateLocalStorageFilters({
      requestType: newRequestType,
      selectedValue: selectedValue,
    });
  };
  const handleCreateNewRequestPass = () => {
    let requestPassJSON = window.localStorage.getItem('requestPass');
    if (requestPassJSON && requestPassJSON !== '') {
      let requestPass = JSON.parse(requestPassJSON);
      if (requestPass.hasChanged) {
        setContinueModal(true);
      } else {
        window.localStorage.removeItem('requestPass');
        navigate('/demande-passage/dossier');
      }
    } else {
      navigate('/demande-passage/dossier');
    }
  };
  const changeSelectValue = (e) => {
    setSelectedValue({ ...selectedValue, [e.target.name]: e.target.value });
    updateLocalStorageFilters({
      requestType: requestType,
      selectedValue: {
        [e.target.name]: e.target.value,
      },
    });
  };

  const onContinueModalClose = (value) => {
    if (value) {
      navigate('/demande-passage/dossier');
    } else {
      window.localStorage.removeItem('requestPass');
      navigate('/demande-passage/dossier');
    }
  };

  const updateLocalStorageFilters = (values) => {
    if (pageFullyLoaded) {
      window.localStorage.setItem('filters', JSON.stringify({
        ...JSON.parse(window.localStorage.getItem('filters')),
        [currentPage]: values,
      }));
    }
  };

  const apiGetListCenters = () => {
    setIsLoading(true);
    axios({ method: 'get', url: `${URL_API}/localisation/centers-by-user`, params: { userId: user.user_id } })
      .then(function(response) {
        setCenters(response.data);
        setIsLoading(false);
        setPageFullyLoaded(true);
      })
      .catch(function(err) {
        logError(alert, err);
      });
  };
  const apiGetListRequestPass = () => {
    if (profil.profil === 5) {
      axios({
        method: 'post',
        url: URL_API + '/demande-passage/liste-by-user',
        data: {
          raison_sociale: user.raison_sociale,
          type: requestType,
          hubs: selectedValue.centerSelected,
        },
      })
        .then((response) => {
          setDemandesPassages(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          logError(alert, error);
        });
    } else {
      axios({
        method: 'post', url: URL_API + '/demande-passage/liste',
        data: {
          type: requestType,
          hubs: selectedValue.centerSelected,
          userId: profil.profil === PROFIL.INTERVENANT ? user.user_id : 0,
        },
      })
      .then((response) => {
        setDemandesPassages(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        logError(alert, error);
      });
    }
  };
  const apiGetBlacklistedUsers = () => {
    axios({
      method: 'get', url: `${URL_API}/blacklist/full-list`, params: { deleted: false },
    })
      .then((response) => {
        let tab = [];

        response.data.forEach((litige) => {
          tab.push(litige.account.raison_sociale);
        });

        dispatch({ type: 'UPDATE_BLACKLISTED_USERS', payload: tab });
      })
      .catch((error) => {
        logError(alert, error);
      });
  };

  return (
    <div style={{ height: 'calc(100% - 30px)' }}>
      <TopBar title='Liste des dossiers' className='topbar-contenu topbar-contenu-vert' color={props.params.color} height={'80px'}
              buttons={(profil.profil === PROFIL.ADMINISTRATEUR || profil.profil === PROFIL.GESTIONNAIRE || profil.profil === PROFIL.CLIENT) &&
                <div className='topbar-buttons'>
                  <MyButton onClick={handleCreateNewRequestPass} icon={faPlus} label={'Nouveau dossier'} sx={{backgroundColor: '#839d3c !important'}}/>
                </div>
              }
              radioSelector={<SelectorTypeDemandePassage leftValue='Supérieurs' rightValue='Autoroutes'
                                                         value={pageFullyLoaded && requestType}
                                                         onChange={(e) => handleOnChange(e)} />}
              filterContainer={
                profil.profil !== PROFIL.NOUVEAU && profil.profil !== PROFIL.CLIENT &&
                  <>
                    <MySelect name={'centerSelected'} label={'Centre'} displayLabel={true}
                              style={{width: '200px'}}
                              multiple={true} value={selectedValue.centerSelected}
                              datas={centers}
                              sx={{backgroundColor: '#839d3c !important', color: 'white !important'}}
                              labelClass={'select-label'}
                              handleChange={changeSelectValue} />
                  </>
              }
      />

      <div className='row' style={{ margin: 0, overflow: 'hidden' }}>
        <ClipLoader color='#0094ca' loading={isLoading} size={75}
                    css={'display: flex; margin: 25px;justify-content: center;'} />
        {!isLoading && demandesPassages && (
          <Paper style={{ height: '100%', width: '100%', borderRadius: '1px !important', borderTop: '1px solid #839d3c' }}>
            <AgGrid
              gridName='ag-grid-request-pass'
              rowData={demandesPassages}
              columnDefs={columns}
              rowHeight={40}
              pagination={false}
              frameworkComponents={frameworkComponents}
              gridOptions={agGridOptions}
              setGridOptions={setAgGridOptions}
              sortable={false}
              type={requestType}
              color='green'
            />
          </Paper>
        )}
      </div>
      <MyDialog
        open={continueModal}
        handleClose={setContinueModal}
        title='Voulez-vous continuer la saisie en cours ?'
        content={'Un dossier est déjà en cours de saisie. Voulez-vous continuer la saisie en cours ?'}
        trueButtonLabel={'Oui'}
        falseButtonLabel={'Non'}
        dialog={onContinueModalClose}
      />
    </div>
  );
};

export default ListeDemandesPassage;
